'use client';

import { RESOURCE_RISK_GRID } from '@/app/components/questionnaireV2/utils/lottie-queries';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { NextButton } from '../next-button/button';
import { GraphicLottie, TypewriterText } from './interstitial-shared';

// TODO: drive from backend/Sanity
const defaultCancerTypes = [
  'Pancreatic Cancer',
  'Head and Neck Cancer',
  'Lymphoma',
  'Bladder Cancer',
  'Liver Cancer',
  'Lung Cancer',
  'Colon Cancer',
  'Thyroid Cancer',
  'Multiple Myeloma',
  'Brain Cancer',
  'Melanoma',
  'Cervical Cancer',
  'Leukemia',
  'Kidney Cancer',
  'Stomach Cancer',
  'Esophageal Cancer',
];

const maleCancerTypes = ['Prostate Cancer', 'Testicular Cancer'];

const femaleCancerTypes = ['Uterine Cancer', 'Breast Cancer', 'Ovarian Cancer'];

const headerText = `We're calculating your risk scores for 21 major cancers`;
const headerTextAfter = 'Next, we’ll sum it all up into your lifetime risk of developing cancer.';
const headerImage = RESOURCE_RISK_GRID;
const paddingClassName = 'px-8'; // 1.5rem

interface CancerItem {
  id: string;
  name: string;
  progress: number;
  isVisible: boolean;
}

/**
 * This component is used to display a list of cancer types and their progress.
 * The list is displayed in a grid with a loading bar and a cancer type name.
 * The loading bar is animated to show the progress of the cancer type.
 * The cancer type name is displayed in a white color.
 * The component is used in the interstitial outro frame.
 *
 * TODO: We need to update the call sites to pass in the gender.
 *
 * @param onNext - The function to call when the animation is complete.
 */
export const CalculatingCancerRisk = ({ onNext, gender }: { onNext: () => void; gender?: 'male' | 'female' }) => {
  let cancers = defaultCancerTypes;

  if (gender === 'male') {
    cancers = [...cancers, ...maleCancerTypes];
  } else if (gender === 'female') {
    cancers = [...cancers, ...femaleCancerTypes];
  }

  const [cancerItems, setCancerItems] = useState<CancerItem[]>(() =>
    cancers.map((name, index) => ({
      id: `cancer-${index}`,
      name,
      progress: 10,
      isVisible: true,
    })),
  );

  const [isTypeWriterComplete, setIsTypeWriterComplete] = useState(false);
  const onTypeWriterComplete = useCallback(() => {
    setIsTypeWriterComplete(true);
  }, []);

  const isAnimationComplete = cancerItems.length === 0;

  // Handle loading bar animation completion
  const handleAnimationComplete = (itemId: string) => {
    setCancerItems((prevItems) => {
      const newItems = [...prevItems];
      const itemIndex = newItems.findIndex((item) => item.id === itemId);

      if (itemIndex !== -1 && newItems[itemIndex].isVisible) {
        newItems[itemIndex] = {
          ...newItems[itemIndex],
          isVisible: false, // Mark for exit animation
        };

        // Remove the item after exit animation completes (300ms)
        setTimeout(() => {
          setCancerItems((currentItems) => currentItems.filter((item) => item.id !== itemId));
        }, 300);
      }

      return newItems;
    });
  };

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header - always at top */}
      {/*After animation is complete full height the container so we can easily center the button. */}
      <div className={`flex w-full flex-col items-center ${isAnimationComplete ? 'h-full' : ''}`}>
        <GraphicLottie srcUrl={headerImage} loop={true} className="w-full" />
        <AnimatePresence mode="wait">
          {isAnimationComplete ? (
            <>
              <div className={`${paddingClassName}`}>
                <TypewriterText
                  content={[{ 
                    _key: `block-${Date.now()}`, 
                    _type: 'block',
                    children: [{ _key: `span-${Date.now()}`, _type: 'span', text: headerTextAfter }] 
                  }]}
                  className={`h-12 text-center text-xl font-bold text-white`}
                  onComplete={onTypeWriterComplete}
                />
              </div>
              <div className="flex h-full w-full items-center justify-center">
                <AnimatePresence>
                  {isTypeWriterComplete && (
                    <motion.div
                      key="action-button"
                      initial={{
                        '--mask-progress': '0%',
                      }}
                      animate={{
                        '--mask-progress': '100%',
                      }}
                      exit={{
                        '--mask-progress': '0%',
                      }}
                      transition={{ duration: 3, ease: 'easeInOut' }}
                      className="flex w-full items-center justify-center"
                      style={
                        {
                          willChange: 'transform',
                          WebkitMask:
                            'linear-gradient(135deg, black var(--mask-progress), transparent var(--mask-progress))',
                          mask: 'linear-gradient(135deg, black var(--mask-progress), transparent var(--mask-progress))',
                        } as React.CSSProperties
                      }
                    >
                      <NextButton
                        text="See My Lifetime Cancer Risk"
                        onNext={onNext}
                        variant="primary"
                        style={{ backgroundImage: 'linear-gradient(90deg, #7CF1C7 0%, #FF9473 164.58%)' }}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </>
          ) : (
            <motion.h1
              key="header-during"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className={`${paddingClassName} pb-4 text-center text-2xl font-bold text-white`}
            >
              {headerText}
            </motion.h1>
          )}
        </AnimatePresence>
      </div>

      {/* Cancer list - takes remaining space with overflow hidden */}
      <div
        className={`${paddingClassName} ${isAnimationComplete ? 'hidden' : 'inherit'} w-full flex-1 overflow-hidden`}
      >
        <AnimatePresence mode="popLayout">
          {cancerItems.map((item, index) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: item.isVisible ? 1 : 0,
                y: 0,
              }}
              exit={{
                opacity: 0,
                y: -20,
                transition: { duration: 0.3, ease: 'easeInOut' },
              }}
              transition={{
                layout: { duration: 0.3, ease: 'easeInOut' },
                opacity: { duration: 0.3 },
                y: { duration: 0.3, ease: 'easeOut' },
              }}
              className="mb-8 grid grid-cols-[1fr_auto] gap-4 last:mb-0"
              style={{ willChange: 'transform, opacity' }}
            >
              {/* Loading bar */}
              <div className="min-w-0">
                <div className="h-3 w-full rounded-full bg-gray-800">
                  <motion.div
                    initial={{ width: '10%' }}
                    animate={{
                      width: '100%',
                    }}
                    transition={{
                      duration: 2,
                      ease: 'easeOut',
                      delay: index * 0.4, // Staggered start times
                    }}
                    onAnimationComplete={() => {
                      handleAnimationComplete(item.id);
                    }}
                    className="h-full rounded-full bg-gradient-to-r from-green-400 to-teal-400"
                    style={{ willChange: 'transform' }}
                  />
                </div>
              </div>

              {/* Cancer text */}
              <div className="w-48">
                <div className="flex h-3 items-center">
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.2, duration: 0.3 }}
                    className="font-medium whitespace-nowrap text-white"
                    style={{ fontSize: '18px' }}
                  >
                    {item.name}
                  </motion.span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};
